import { useEffect, useMemo, useRef, useState } from 'react'
import { Autocomplete, CircularProgress, TextField } from '@mui/material'
import apiClient from '../../utils/apiClient'
import organizationSessionStore from '../../stores/organizationSessionStore'

function optionLabel(client) {
	if (!client) return ''
	return [client.name, client.phone, client.email].filter(Boolean).join(' · ')
}

export default function ClientPicker({ value, selectedClient = null, onChange, disabled = false, label = 'Клиент' }) {
	const [open, setOpen] = useState(false)
	const [inputValue, setInputValue] = useState('')
	const [searchQuery, setSearchQuery] = useState('')
	const [options, setOptions] = useState([])
	const [selected, setSelected] = useState(
		selectedClient && String(selectedClient.id) === String(value) ? selectedClient : null
	)
	const [loading, setLoading] = useState(false)
	const requestVersion = useRef(0)

	useEffect(() => {
		if (!value) {
			setSelected(null)
			setInputValue('')
			return
		}
		if (selectedClient && String(selectedClient.id) === String(value)) {
			setSelected(selectedClient)
			setInputValue(optionLabel(selectedClient))
			return
		}
		setSelected(current => String(current?.id) === String(value) ? current : null)
	}, [value, selectedClient])

	useEffect(() => {
		if (!open) {
			requestVersion.current += 1
			setLoading(false)
			return undefined
		}
		const generation = organizationSessionStore.contextGeneration
		const version = ++requestVersion.current
		const timer = setTimeout(async () => {
			setLoading(true)
			try {
				const response = await apiClient.get('/api/clients', { params: { q: searchQuery.trim(), limit: 20 } })
				if (version === requestVersion.current && generation === organizationSessionStore.contextGeneration) {
					setOptions(Array.isArray(response.data?.clients) ? response.data.clients : [])
				}
			} catch (_) {
				if (version === requestVersion.current && generation === organizationSessionStore.contextGeneration) setOptions([])
			} finally {
				if (version === requestVersion.current && generation === organizationSessionStore.contextGeneration) setLoading(false)
			}
		}, 250)
		return () => clearTimeout(timer)
	}, [open, searchQuery])

	const visibleOptions = useMemo(() => {
		if (!selected || options.some(option => String(option.id) === String(selected.id))) return options
		return [selected, ...options]
	}, [options, selected])

	return <Autocomplete
		fullWidth
		disabled={disabled}
		open={open}
		onOpen={() => setOpen(true)}
		onClose={() => setOpen(false)}
		options={visibleOptions}
		value={selected}
		inputValue={inputValue}
		onInputChange={(_event, next, reason) => {
			setInputValue(next)
			if (reason === 'input' || reason === 'clear') setSearchQuery(next)
		}}
		onChange={(_event, next) => {
			setSelected(next || null)
			setInputValue(optionLabel(next))
			setSearchQuery('')
			setOpen(false)
			onChange(next?.id || '', next || null)
		}}
		getOptionLabel={optionLabel}
		isOptionEqualToValue={(option, current) => String(option.id) === String(current.id)}
		loading={loading}
		noOptionsText={searchQuery.trim() ? 'Клиенты не найдены' : 'Начните вводить имя, телефон или электронную почту'}
		renderInput={params => <TextField {...params} label={label} InputProps={{ ...params.InputProps, endAdornment: <>{loading ? <CircularProgress color='inherit' size={18} /> : null}{params.InputProps.endAdornment}</> }} />}
	/>
}
